import React, { useMemo, useCallback, useRef, useEffect, useContext } from "react";
import { Input, Space } from "antd";
import type { PasswordProps as AntPasswordProps } from "antd/es/input";
import { useAndStore } from "../../base/store/useAndStore";
import { getDataSetStoreKey } from "../../base/storeKeyUtils";
import { parseChangeAction, createCommand } from "../../base/actionParser";
import { useFieldValidation } from "../../base/useFieldValidation";
import { mergeStoreProps } from "../../base/storePropsUtils";
import { prepareAntdDomProps } from "../../utils/normalizeReactStyle";
import { ValidateHelp } from "../../base/ValidateHelp";
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from "../../types/common";
import { DataSetInheritContext } from "../../base/DataSetInheritContext";
import { resolveAffixRenderConfig, SPACE_ADDON_NOWRAP_STYLE } from "../../utils/inputAffixUtils";

const { Password } = Input;
const VALIDATE_DEBOUNCE_MS = 200;

export interface AndInputPasswordProps
  extends Omit<
      AntPasswordProps,
      "onChange" | "prefix" | "suffix" | "addonBefore" | "addonAfter"
    >,
    BaseComponentProps,
    DataSetConfig,
    ValidationDisplayConfig {
  prefix?: string;
  suffix?: string;
  addonBefore?: string;
  addonAfter?: string;
  rules?: Array<{ type?: string; required?: boolean; message?: string; pattern?: string | RegExp; min?: number; max?: number }>;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  changeAction?: string;
  pressEnterAction?: string;
  clearAction?: string;
  onChange?: (value: string, e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const AndInputPassword: React.FC<AndInputPasswordProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  rules,
  validateOnBlur,
  validateOnChange,
  showValidateHelp = true,
  changeAction,
  pressEnterAction,
  clearAction,
  onChange,
  value: propValue,
  defaultValue,
  ...restProps
}) => {
  const ctx = useContext(DataSetInheritContext);
  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);
  const validateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );
  const storeProps = useAndStore((s) =>
    sessionId && id ? s.propsStore[`${sessionId}_props`]?.[id] ?? null : null
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && dataSetField ? s.getDataSet(dataSetKey, dataSetField) : undefined
  );

  const { validate, help } = useFieldValidation({
    rules,
    dataSet,
    dataSetField,
    sessionId,
    id,
    develop,
  });

  const displayValue = useMemo(() => {
    if (storeValue !== undefined) return storeValue;
    if (storeProps?.value !== undefined) return storeProps.value;
    return propValue ?? defaultValue;
  }, [storeValue, storeProps?.value, propValue, defaultValue]);

  const mergedProps = useMemo(
    () => mergeStoreProps(restProps as Record<string, unknown>, storeProps ?? undefined),
    [restProps, storeProps]
  );

  const { prefixIconNode, suffixIconNode, prefixAddonText, suffixAddonText, inputRest } = useMemo(() => {
    const m = mergedProps as Record<string, unknown>;
    const {
      prefix: p,
      suffix: s,
      addonBefore: ab,
      addonAfter: aa,
      ...rest
    } = m;
    const prefixConfig = resolveAffixRenderConfig({
      iconName: p === undefined || p === null ? undefined : String(p),
      label: ab === undefined || ab === null ? undefined : String(ab),
    });
    const suffixConfig = resolveAffixRenderConfig({
      iconName: s === undefined || s === null ? undefined : String(s),
      label: aa === undefined || aa === null ? undefined : String(aa),
    });
    return {
      prefixIconNode: prefixConfig.iconNode,
      suffixIconNode: suffixConfig.iconNode,
      prefixAddonText: prefixConfig.iconConfigured ? undefined : prefixConfig.labelText,
      suffixAddonText: suffixConfig.iconConfigured ? undefined : suffixConfig.labelText,
      inputRest: rest,
    };
  }, [mergedProps]);

  const inputDomProps = useMemo(
    () => prepareAntdDomProps(inputRest as Record<string, unknown>),
    [inputRest],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (onChange) {
        onChange(newValue, e);
        return;
      }
      if (dataSetKey && dataSetField) setDataSet(dataSetKey, dataSetField, newValue);
      if (changeAction && sessionId) {
        const { actionName, arg } = parseChangeAction(changeAction, newValue, {
          id,
          dataSet,
          dataSetField,
        }, ctx);
        addCommand(sessionId, createCommand(actionName, arg));
      }
      if (validateOnChange) {
        if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
        validateTimerRef.current = setTimeout(() => {
          validateTimerRef.current = null;
          validate(newValue);
        }, VALIDATE_DEBOUNCE_MS);
      }
    },
    [
      onChange,
      dataSetKey,
      dataSetField,
      setDataSet,
      changeAction,
      sessionId,
      id,
      addCommand,
      validateOnChange,
      validate,
      ctx,
    ]
  );

  const handleBlur = useCallback(() => {
    if (!validateOnBlur) return;
    validate(displayValue);
  }, [validateOnBlur, validate, displayValue]);

  const handlePressEnter = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const onPressEnter = (inputDomProps as { onPressEnter?: (ev: React.KeyboardEvent<HTMLInputElement>) => void })
        .onPressEnter;
      if (onPressEnter) {
        onPressEnter(e);
        return;
      }
      if (pressEnterAction && sessionId) {
        const { actionName, arg } = parseChangeAction(pressEnterAction, displayValue, {
          id,
          dataSet,
          dataSetField,
        }, ctx);
        addCommand(sessionId, createCommand(actionName, arg));
      }
    },
    [
      inputDomProps,
      pressEnterAction,
      sessionId,
      displayValue,
      id,
      dataSet,
      dataSetField,
      addCommand,
      ctx,
    ]
  );

  const handleClear = useCallback(() => {
    const userOnClear = (inputDomProps as { onClear?: () => void }).onClear;
    if (userOnClear) {
      userOnClear();
      return;
    }
    if (clearAction && sessionId) {
      const { actionName, arg } = parseChangeAction(clearAction, "", {
        id,
        dataSet,
        dataSetField,
      }, ctx);
      addCommand(sessionId, createCommand(actionName, arg));
    }
  }, [inputDomProps, clearAction, sessionId, id, dataSet, dataSetField, addCommand, ctx]);

  useEffect(
    () => () => {
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    },
    []
  );

  return (
    <div style={{ width: "100%" }}>
      <Space.Compact style={{ width: "100%" }}>
        {prefixAddonText ? <Space.Addon style={SPACE_ADDON_NOWRAP_STYLE}>{prefixAddonText}</Space.Addon> : null}
        <Password
          {...(inputDomProps as object)}
          prefix={prefixIconNode}
          suffix={suffixIconNode}
          disabled={develop ? false : (inputDomProps as { disabled?: boolean }).disabled}
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onPressEnter={handlePressEnter}
          onClear={handleClear}
          status={help ? ("error" as const) : undefined}
        />
        {suffixAddonText ? <Space.Addon style={SPACE_ADDON_NOWRAP_STYLE}>{suffixAddonText}</Space.Addon> : null}
      </Space.Compact>
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </div>
  );
};

export default AndInputPassword;
